import React, { useState } from 'react';
import { useOperationsHead } from './hooks/useOperationsHead';

const VendorChangeApproval = () => {
  const { vendorChangeRequests, approveVendorChange } = useOperationsHead();
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [notes, setNotes] = useState('');

  const handleApproval = async (orderId: string, approved: boolean) => {
    try {
      await approveVendorChange.mutateAsync({
        orderId,
        approved,
        notes
      });
      setSelectedRequest(null);
      setNotes('');
    } catch (error) {
      console.error('Error processing vendor change:', error);
    }
  };

  return (
    <div className="p-6 bg-white">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Vendor Change Approvals</h1>
        <p className="text-gray-600">Review and approve vendor change requests from HO Manager</p>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {vendorChangeRequests.data?.map((request: any) => (
            <li key={request.id}>
              <div className="px-4 py-4 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-indigo-600 truncate">
                        Order #{request.order_number}
                      </p>
                      <div className="ml-2 flex-shrink-0 flex">
                        <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          Pending Approval
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 sm:flex sm:justify-between">
                      <div className="sm:flex">
                        <p className="flex items-center text-sm text-gray-500">
                          <i className="ri-user-line mr-1.5 h-4 w-4 text-gray-400"></i>
                          {request.customers?.name}
                        </p>
                        <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                          <i className="ri-shopping-bag-line mr-1.5 h-4 w-4 text-gray-400"></i>
                          {request.products?.model_name}
                        </p>
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                        <i className="ri-calendar-line mr-1.5 h-4 w-4 text-gray-400"></i>
                        <p>
                          Requested {new Date(request.change_requested_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-red-50 p-3 rounded-lg">
                        <h4 className="text-sm font-medium text-red-800">Current Vendor</h4>
                        <p className="text-sm text-red-600">{request.vendors?.name}</p>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg">
                        <h4 className="text-sm font-medium text-green-800">Requested Vendor</h4>
                        <p className="text-sm text-green-600">{request.requested_vendor?.name}</p>
                      </div>
                    </div>

                    <div className="mt-3">
                      <h4 className="text-sm font-medium text-gray-700">Reason for Change</h4>
                      <p className="text-sm text-gray-600 mt-1">{request.vendor_change_reason}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex space-x-3">
                  <button
                    onClick={() => setSelectedRequest(request)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700"
                  >
                    Review
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>

        {vendorChangeRequests.data?.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-file-list-line text-4xl text-gray-400 mb-4"></i>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Pending Requests</h3>
            <p className="text-gray-500">All vendor change requests have been processed.</p>
          </div>
        )}
      </div>

      {/* Review Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Review Vendor Change Request
                </h3>
                <button
                  onClick={() => setSelectedRequest(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-700">Order Details</h4>
                  <div className="mt-2 bg-gray-50 p-3 rounded">
                    <p><strong>Order:</strong> #{selectedRequest.order_number}</p>
                    <p><strong>Customer:</strong> {selectedRequest.customers?.name}</p>
                    <p><strong>Product:</strong> {selectedRequest.products?.model_name}</p>
                    <p><strong>Amount:</strong> ₹{selectedRequest.total_amount?.toLocaleString()}</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-700">Vendor Change Details</h4>
                  <div className="mt-2 grid grid-cols-2 gap-4">
                    <div className="bg-red-50 p-3 rounded">
                      <p className="text-sm font-medium text-red-800">From</p>
                      <p className="text-red-600">{selectedRequest.vendors?.name}</p>
                    </div>
                    <div className="bg-green-50 p-3 rounded">
                      <p className="text-sm font-medium text-green-800">To</p>
                      <p className="text-green-600">{selectedRequest.requested_vendor?.name}</p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700">Reason</p>
                    <p className="text-sm text-gray-600 mt-1">{selectedRequest.vendor_change_reason}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Approval Notes
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Add notes for your decision..."
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => handleApproval(selectedRequest.id, true)}
                    disabled={approveVendorChange.isPending}
                    className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    {approveVendorChange.isPending ? 'Processing...' : 'Approve Change'}
                  </button>
                  <button
                    onClick={() => handleApproval(selectedRequest.id, false)}
                    disabled={approveVendorChange.isPending}
                    className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
                  >
                    {approveVendorChange.isPending ? 'Processing...' : 'Reject Change'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorChangeApproval;